import { baseApi } from "./baseApi";

export interface Admin {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role: "ADMIN" | "STAFF" | "SUPER_ADMIN";
  isSuspended: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdminData {
  email: string;
  name: string;
  phone?: string;
  role?: "ADMIN" | "STAFF" | "SUPER_ADMIN";
}

export interface UpdateAdminData {
  name?: string;
  phone?: string;
  role?: "ADMIN" | "STAFF" | "SUPER_ADMIN";
  password?: string;
}

export interface ValidateTokenResponse {
  email: string;
  name: string;
  role: string;
}

export interface SetPasswordData {
  token: string;
  password: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface AdminFilters {
  page?: number;
  limit?: number;
}

export interface Customer {
  id: string;
  email: string;
  name: string;
  phone?: string | null;
  role: "CUSTOMER";
  isSuspended: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    orders: number;
    addresses: number;
  };
}

export interface CustomerFilters {
  page?: number;
  limit?: number;
  search?: string;
}

export interface PaginatedCustomersResponse {
  success: boolean;
  data: Customer[];
  pagination: PaginationMeta;
  message?: string;
}

export interface PaginatedAdminsResponse {
  success: boolean;
  data: Admin[];
  pagination: PaginationMeta;
  message?: string;
}

/**
 * Admin API slice
 * Handles admin management API calls (super admin only)
 */
export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all admins with pagination
    getAllAdmins: builder.query<PaginatedAdminsResponse, AdminFilters | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.limit) searchParams.append("limit", String(params.limit));
        const queryString = searchParams.toString();
        return {
          url: `/admin${queryString ? `?${queryString}` : ""}`,
          credentials: "include",
        };
      },
      providesTags: ["Admin"],
    }),

    // Get admin by ID
    getAdminById: builder.query<
      { success: boolean; data: Admin; message?: string },
      string
    >({
      query: (id) => ({
        url: `/admin/${id}`,
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "Admin", id }],
    }),

    // Create admin
    createAdmin: builder.mutation<
      { success: boolean; data: Admin; message?: string },
      CreateAdminData
    >({
      query: (data) => ({
        url: "/admin",
        method: "POST",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: ["Admin"],
    }),

    // Update admin
    updateAdmin: builder.mutation<
      { success: boolean; data: Admin; message?: string },
      { id: string; data: UpdateAdminData }
    >({
      query: ({ id, data }) => ({
        url: `/admin/${id}`,
        method: "PUT",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Admin", id },
        "Admin",
      ],
    }),

    // Delete admin
    deleteAdmin: builder.mutation<
      { success: boolean; message?: string },
      string
    >({
      query: (id) => ({
        url: `/admin/${id}`,
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: ["Admin"],
    }),

    // Validate setup token (for set password page)
    validateSetupToken: builder.mutation<
      { success: boolean; data: ValidateTokenResponse; message?: string },
      string
    >({
      query: (token) => ({
        url: "/auth/validate-setup-token",
        method: "POST",
        body: { token },
      }),
    }),

    // Set password (for new admins)
    setPassword: builder.mutation<
      { success: boolean; message?: string },
      SetPasswordData
    >({
      query: (data) => ({
        url: "/auth/set-password",
        method: "POST",
        body: data,
      }),
    }),

    // Resend invitation email
    resendInvitation: builder.mutation<
      { success: boolean; message?: string },
      string
    >({
      query: (adminId) => ({
        url: "/auth/resend-invitation",
        method: "POST",
        body: { adminId },
        credentials: "include",
      }),
    }),

    // Suspend admin
    suspendAdmin: builder.mutation<
      { success: boolean; data: Admin; message?: string },
      string
    >({
      query: (id) => ({
        url: `/admin/${id}/suspend`,
        method: "POST",
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Admin", id },
        "Admin",
      ],
    }),

    // Unsuspend admin
    unsuspendAdmin: builder.mutation<
      { success: boolean; data: Admin; message?: string },
      string
    >({
      query: (id) => ({
        url: `/admin/${id}/unsuspend`,
        method: "POST",
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Admin", id },
        "Admin",
      ],
    }),

    // Get all customers with pagination
    getAllCustomers: builder.query<PaginatedCustomersResponse, CustomerFilters | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.limit) searchParams.append("limit", String(params.limit));
        if (params?.search) searchParams.append("search", params.search);
        const queryString = searchParams.toString();
        return {
          url: `/customers${queryString ? `?${queryString}` : ""}`,
          credentials: "include",
        };
      },
      providesTags: ["Customer"],
    }),
  }),
});

export const {
  useGetAllAdminsQuery,
  useGetAdminByIdQuery,
  useCreateAdminMutation,
  useUpdateAdminMutation,
  useDeleteAdminMutation,
  useValidateSetupTokenMutation,
  useSetPasswordMutation,
  useResendInvitationMutation,
  useSuspendAdminMutation,
  useUnsuspendAdminMutation,
  useGetAllCustomersQuery,
} = adminApi;
